// ─────────────────────────────────────────────
//  TeamPage — the department contact directory, on the web.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The landing's FIFTH door, and a different KIND of destination from the other
//  four. Board, My Rooms, Timeline and Messages are the day being worked; this
//  is a reference book you open, find one number in, and close. That is why it
//  is a full-width plaque under the four on /home rather than a fifth tile
//  among them, and why this page is READ-ONLY end to end: it renders three of
//  ATC's own collections — `staff`, `surgeons`, `facilities` — and writes to
//  none of them, and it never touches `users`, whose login documents carry push
//  tokens and facility membership that a contact list has no business showing.
//
//  ⚠️ THE PUBLIC GOOGLE SHEET IS GONE (owner, 2026-08-19: "I want nothing
//  public"). This page used to read one document mirrored weekly out of a
//  world-readable file. It reads ATC's own records now, which is also why a row
//  can finally say whether someone is a CRNA or an AA.
//
//  This file is only the lobby chrome. The directory itself is
//  components/TeamDirectory, because the native shell renders the SAME
//  component inside its own header and tab bar — a second, mobile-shaped copy
//  of a searchable directory is how two directories start disagreeing about who
//  works here.
//
//  ⚠️ THE PAGE DOES NOT SCROLL; THE LIST DOES. The search field is the whole
//  point of the surface and must never leave the screen — a page-level
//  scrollbar carries it off the top exactly when someone is retyping a name.
//  Same shape MessagesPage uses for the same reason.
//
//  ⚠️ THREE ROLES AS A POSITIVE ALLOWLIST, AND A CAMPUS. This is the personal
//  phone number and inbox of everyone in the department. Only providers (CRNAs, AAs, residents), anesthesiologists
//  (an AIC is a doctor with isAIC) and admins may read it — observers,
//  schedulers and the unattended display accounts may not — AND the account
//  must belong to one of this tenant's hospitals (Wesley Chapel, Carrollwood,
//  Tampa). Role alone was not enough: a freshly self-provisioned login carries
//  `facilityIds: []` and could hold the role `provider` while belonging to no
//  hospital at all. That two-part test is enforced FOUR times and they must
//  agree: the Firestore rules (the only one that actually protects anything),
//  the route gates below in App.js, the door on /home, and the in-page check
//  inside TeamDirectory. The last is not redundant — the native shell mounts
//  TeamDirectory through a different route tree where the web gates never run.
//  A wall-mounted television in a corridor must never render this, and nobody
//  who is refused should ever see a blank page instead of a sentence.
// ─────────────────────────────────────────────
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Avatar, SignOutButton } from "../components";
import NavMenu from "../components/NavMenu";
import TeamDirectory from "../components/TeamDirectory";
import tokens from "../theme/tokens";

const { color, space, font } = tokens;
const { lobby } = color;

export default function TeamPage() {
  const navigate = useNavigate();
  const { user, isDoctor } = useAuth();

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.topbarLeft}>
          <NavMenu />
        </div>
        <div style={s.topbarRight}>
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      <main style={s.main} aria-label="Team directory">
        <TeamDirectory />
      </main>
    </div>
  );
}

const s = {
  page: {
    // Fixed viewport height with the list scrolling inside it — see the header.
    height: "100vh",
    background: lobby.linen,
    color: color.text.primary,
    fontFamily: font.family,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // The same 56px sticky geometry as the board and the lobby, warmed to the
  // lobby surface, so moving between them never shifts the chrome.
  topbar: {
    background: lobby.plaque,
    borderBottom: `1px solid ${lobby.sand}`,
    padding: "0 20px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  topbarLeft:  { display: "flex", alignItems: "center", gap: space.sm, minWidth: 0 },
  topbarRight: { display: "flex", alignItems: "center", gap: space.sm },

  main: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
};
